import { useId, useMemo, useState } from 'react';
import { EmptyState } from '../../../components/PageState';
import { Badge, Button, FilterChips, Input, LinkButton, Modal, type ChipOption } from '../../../components/ui';
import { formatCount, formatRatio } from '../../../lib/format';
import type { DesempenhoDoModelo } from '../comparar';
import type { MessageTemplateSummary, SegmentFamily, SenderKind, TemplateCategory, TemplateSector } from '../types';
import { SEGMENT_FAMILY_LABEL, TEMPLATE_APPROVAL_LABEL, TEMPLATE_APPROVAL_TONE, TEMPLATE_CATEGORY_LABEL, TEMPLATE_CATEGORY_TONE, TEMPLATE_SECTOR_LABEL, normalizarBusca } from '../vocabulario';

/**
 * Seletor de modelo de mensagem.
 *
 * Decisoes nao obvias:
 *  1. Contadores dos chips sao da lista INTEIRA, nao do recorte: a pessoa
 *     precisa saber que existem 4 de marketing mesmo com a busca vazia.
 *  2. Modelos que COMBINAM com o publico marcado vem primeiro, com selo;
 *     os outros continuam na lista (a equipe pode ter um motivo).
 *  3. Cada modelo mostra o desempenho real nos envios do periodo (quantos
 *     envios, taxa de pagamento): escolher pelo que funcionou, nao pelo nome.
 *  4. Pela API oficial, modelo nao aprovado aparece mas nao pode ser
 *     escolhido -- esconder faria a equipe procurar um modelo que "sumiu".
 */

type FiltroCategoria = 'todas' | TemplateCategory;
type FiltroSetor = 'todos' | TemplateSector;

function plural(n: number, singular: string, pluralForm: string): string {
  return `${formatCount(n)} ${n === 1 ? singular : pluralForm}`;
}

export function ModalEscolherModelo({
  open,
  onClose,
  templates,
  selectedId,
  onSelect,
  senderKind,
  familias,
  desempenho,
}: {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly templates: readonly MessageTemplateSummary[];
  readonly selectedId: string | null;
  readonly onSelect: (template: MessageTemplateSummary) => void;
  readonly senderKind: SenderKind | null;
  /** Familias do publico marcado; vazio = sem sugestao. */
  readonly familias: readonly SegmentFamily[];
  readonly desempenho: ReadonlyMap<string, DesempenhoDoModelo>;
}): JSX.Element | null {
  const buscaId = useId();
  const [busca, setBusca] = useState('');
  const [categoria, setCategoria] = useState<FiltroCategoria>('todas');
  const [setor, setSetor] = useState<FiltroSetor>('todos');

  const contar = (fn: (t: MessageTemplateSummary) => boolean): string => formatCount(templates.filter(fn).length);

  const opcoesCategoria: readonly ChipOption<FiltroCategoria>[] = [
    { value: 'todas', label: 'Todas', count: formatCount(templates.length) },
    { value: 'marketing', label: 'Marketing', count: contar((t) => t.category === 'marketing') },
    { value: 'utility', label: 'Utility', count: contar((t) => t.category === 'utility') },
  ];
  const opcoesSetor: readonly ChipOption<FiltroSetor>[] = [
    { value: 'todos', label: 'Todos', count: formatCount(templates.length) },
    { value: 'comercial', label: 'Comercial', count: contar((t) => t.sector === 'comercial') },
    { value: 'funil', label: 'Funil', count: contar((t) => t.sector === 'funil') },
    { value: 'suporte', label: 'Suporte', count: contar((t) => t.sector === 'suporte') },
  ];

  const combina = (t: MessageTemplateSummary): boolean => familias.length > 0 && familias.every((f) => t.fits.includes(f));

  const filtrados = useMemo(() => {
    const termo = normalizarBusca(busca.trim());
    const lista = templates.filter(
      (t) =>
        (categoria === 'todas' || t.category === categoria) &&
        (setor === 'todos' || t.sector === setor) &&
        (termo === '' || normalizarBusca(`${t.name} ${t.body}`).includes(termo)),
    );
    const casa = (t: MessageTemplateSummary): number => (familias.length > 0 && familias.every((f) => t.fits.includes(f)) ? 0 : 1);
    return [...lista].sort((a, b) => casa(a) - casa(b));
  }, [templates, busca, categoria, setor, familias]);

  const bloqueado = (t: MessageTemplateSummary): boolean => senderKind === 'oficial' && t.approval !== 'aprovado';

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Escolher modelo de mensagem"
      description={
        familias.length === 0
          ? 'Pela API oficial só saem modelos aprovados na Meta; pela Evolution também sai texto livre.'
          : `Primeiro os modelos escritos para ${familias.map((f) => SEGMENT_FAMILY_LABEL[f].toLowerCase()).join(' e ')}, o público marcado.`
      }
      size="lg"
      footer={
        <>
          <LinkButton to="/admin/modelos" variant="secondary" icon="mais">
            Criar modelo
          </LinkButton>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div>
          <label htmlFor={buscaId} className="sr-only">
            Buscar modelo por nome ou texto
          </label>
          <Input id={buscaId} type="search" placeholder="Buscar por nome ou texto" value={busca} onChange={(e) => setBusca(e.target.value)} autoComplete="off" />
        </div>
        <FilterChips options={opcoesCategoria} value={categoria} onChange={setCategoria} label="Categoria" prefix="Categoria:" />
        <FilterChips options={opcoesSetor} value={setor} onChange={setSetor} label="Setor" prefix="Setor:" tone="accent" />

        {filtrados.length === 0 ? (
          <EmptyState title="Nenhum modelo com esses filtros" description="Limpe a busca ou troque a categoria. Para um texto novo, crie um modelo na tela de Modelos." />
        ) : (
          <ul className="flex flex-col gap-2" aria-label="Modelos de mensagem">
            {filtrados.map((t) => {
              const escolhido = t.id === selectedId;
              const travado = bloqueado(t);
              const d = desempenho.get(t.id);
              return (
                <li key={t.id}>
                  <button
                    type="button"
                    aria-pressed={escolhido}
                    disabled={travado}
                    onClick={() => {
                      onSelect(t);
                      onClose();
                    }}
                    className={`w-full rounded-xl border px-4 py-3 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                      escolhido ? 'border-brand bg-brand/5' : 'border-line bg-surface hover:border-brand/50'
                    }`}
                  >
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <p className="font-medium text-ink">{t.name}</p>
                      <div className="flex flex-wrap gap-1.5">
                        {combina(t) ? (
                          <Badge tone="good" icon="check">
                            Combina com o público
                          </Badge>
                        ) : null}
                        <Badge tone={TEMPLATE_CATEGORY_TONE[t.category]}>{TEMPLATE_CATEGORY_LABEL[t.category]}</Badge>
                        <Badge tone={TEMPLATE_APPROVAL_TONE[t.approval]} dot>
                          {TEMPLATE_APPROVAL_LABEL[t.approval]}
                        </Badge>
                      </div>
                    </div>
                    <p className="mt-1 text-xs text-ink-subtle">
                      {TEMPLATE_SECTOR_LABEL[t.sector]} · para {t.fits.map((f) => SEGMENT_FAMILY_LABEL[f].toLowerCase()).join(' e ')} · {plural(t.variables.length, 'variável', 'variáveis')} ·{' '}
                      {plural(t.buttons.length, 'botão', 'botões')}
                    </p>
                    <p className="mt-1.5 line-clamp-2 text-sm text-ink-muted">{t.body}</p>
                    <p className="mt-1.5 text-xs text-ink-subtle">
                      {d === undefined || d.envios === 0
                        ? 'Ainda sem envio no período.'
                        : `${plural(d.envios, 'envio', 'envios')} no período · ${formatRatio(d.pagaram, d.entregues)} dos que receberam pagaram`}
                    </p>
                    {travado ? <p className="mt-1.5 text-xs text-status-warning">Só sai pela API oficial depois da aprovação da Meta. Troque o número de saída para a Evolution ou escolha outro modelo.</p> : null}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </Modal>
  );
}
